import { request, ApiError, refreshAccessToken, isTokenExpiringSoon } from './core'
import { logger } from '@utils/logger'
import { documentsApi } from './documents'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentCloneRequest,
  AgentCloneResponse,
  AgentCloneDependenciesResponse,
  Simulation,
  SimulationCreate,
  AgentTemplatesResponse,
  RegistrationTemplateCatalogResponse,
  RegistrationTemplatePreviewResponse,
  RegistrationTemplateApplyRequest,
  RegistrationTemplateApplyResponse,
  RegistryAgentList,
  RegistryAgentItem,
  AgentRegistryRequest,
} from './types'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const { api: apiConfig, auth: authConfig } = config
const tagpFetch = createServiceFetch(apiConfig.baseUrl)

async function requestBlob(
  endpoint: string,
  options?: { accept?: string; tenantId?: string },
): Promise<{ blob: Blob; filename: string }> {
  const accept = options?.accept || 'text/csv'
  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    /* ignore */
  }
  if (token && isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken()
    } catch {
      /* proceed */
    }
  }
  let tenantId: string | null = null
  if (options?.tenantId !== undefined) {
    tenantId = options.tenantId || null
  } else {
    try {
      tenantId = localStorage.getItem('tagp-tenant-id')
    } catch {
      /* ignore */
    }
  }
  const headers: Record<string, string> = {
    Accept: accept,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
  }
  const url = `${apiConfig.baseUrl}${endpoint}`
  let response = await tagpFetch(url, { headers })
  if (response.status === 401) {
    try {
      const newToken = await refreshAccessToken()
      response = await tagpFetch(url, {
        headers: { ...headers, Authorization: `Bearer ${newToken}` },
      })
    } catch {
      /* keep 401 */
    }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      (errorData as { detail?: string })?.detail || `Download failed (${response.status})`,
      response.status,
      errorData,
    )
  }
  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="?([^";]+)"?/)
  const fallbackName = accept.includes('json') ? 'download.json' : 'download.csv'
  return { blob, filename: match?.[1] || fallbackName }
}

export interface RegistryJsonExportParams {
  project_id?: string
  search?: string
  registry_status?: 'registered' | 'archived'
  created_by?: string
  agent_type?: 'native' | 'external'
  catalog_meta?: string
  framework?: string
}

export interface AgentDeletePrerequisitesRead {
  agent_id: string
  is_registry_registered: boolean
  has_active_store_listing: boolean
  active_listing_ids: string[]
  non_archived_deployment_count: number
  non_archived_deployment_ids: string[]
}

export interface VirtualAgentLinkedArtifact {
  artifact_type: 'agent' | 'workflow'
  artifact_id: string
  artifact_name: string | null
  builder_session_id: string
  title: string | null
}

export interface VirtualAgentLinkedArtifactsResponse {
  virtual_agent_id: string
  project_id: string
  items: VirtualAgentLinkedArtifact[]
}

export interface RegistrySystemAgent {
  id: string
  name: string
  description: string
  framework: string
}

export interface AgentChatAccess {
  allowed: boolean
  environment: string
  agent_id: string
}

export interface AgentEditPresenceEditor {
  user_id: string
  display_name: string
}

export interface AgentEditPresence {
  agent_id: string
  is_self: boolean
  editor: AgentEditPresenceEditor | null
}

export const agentsApi = {
  /** Unpaginated list for dropdowns/selectors. Returns all agents matching filters.
   * Pass `page` + `page_size` to fetch a single page (server-side pagination) —
   * used by the Guardrails tabs so they only load the visible page. */
  listAll: (params?: {
    project_id?: string
    status?: string
    framework?: string
    search?: string
    created_by?: string
    include_archived?: boolean
    /**
     * Code-defined virtual agents (Product Docs Bot, ABA, CWB).
     * Default true on the API for Benchmark Studio / Evals.
     * Pass false for Agent Workflows and other graph-wiring pickers.
     */
    include_virtual_agents?: boolean
    /**
     * When project_id is set, also include native agents the user has approved
     * Agent Store access to (including other projects).
     */
    include_store_access?: boolean
    /** 1-based page number. Set with page_size for server-side pagination. */
    page?: number
    /** Page size (max 250). Set with page for server-side pagination. */
    page_size?: number
    /** Skip loading the fat graph_definition JSON column (callers needing only
     *  agent identity pass true; Guardrails Coverage must leave it false). */
    defer_graph_definition?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Agent[]; total: number }>(`/agents/list-all${query}`, { signal })
  },

  list: (params?: {
    project_id?: string
    status?: string
    registry_status?: string
    framework?: string
    search?: string
    created_by?: string
    include_archived?: boolean
    page?: number
    page_size?: number
    order_by?: string
    order_desc?: boolean
    is_favourite?: boolean
    signal?: AbortSignal
    tenantId?: string
  }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Agent[]; total: number }>(`/agents${query}`, { signal, tenantId })
  },

  /**
   * List agents in the Agent Registry (native + external) as a single paginated list.
   * Use this for the Agent Registry page so pagination applies to the merged list.
   */
  registryList: (params?: {
    project_id?: string
    framework?: string
    search?: string
    created_by?: string
    registry_status?: 'registered' | 'archived'
    system_agent_ids?: string
    /** native = Studio agents only; external = registered external agents only. Omit for both. */
    agent_type?: string
    page?: number
    page_size?: number
    order_by?: string
    order_desc?: boolean
    scope?: 'all'
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<RegistryAgentList>(`/agents/registry${query}`, { signal })
  },

  /** Code-defined system agents for Agent Registry quick filters. */
  registrySystemAgents: (params?: { signal?: AbortSignal }) => {
    const { signal } = params || {}
    return request<RegistrySystemAgent[]>('/agents/registry/system-agents', { signal })
  },

  /**
   * List all Agent Registry items (native + external) without pagination.
   * Use for dropdown/select components that need a complete option set.
   */
  registryListUnpaginated: (params?: {
    project_id?: string
    framework?: string
    search?: string
    created_by?: string
    order_by?: string
    order_desc?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<RegistryAgentItem[]>(`/agents/registry/unpaginated${query}`, { signal })
  },

  virtualLinkedArtifacts: (params: {
    virtualAgentId: string
    project_id: string
    signal?: AbortSignal
  }) => {
    const { virtualAgentId, signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return request<VirtualAgentLinkedArtifactsResponse>(
      `/agents/virtual/${encodeURIComponent(virtualAgentId)}/linked-artifacts${query}`,
      { signal }
    )
  },

  get: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<Agent>(`/agents/${agentId}`, { signal: options?.signal }),

  /**
   * Env-aware chat authorization (TEST/DEV/QA = project membership;
   * UAT/PROD = membership or Agent Store grant).
   */
  getChatAccess: (
    agentId: string,
    environment: string,
    options?: { signal?: AbortSignal }
  ) =>
    request<AgentChatAccess>(
      `/agents/${agentId}/chat-access${buildQueryString({ environment })}`,
      { signal: options?.signal }
    ),

  getEditPresence: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<AgentEditPresence>(`/agents/${agentId}/edit-presence`, {
      signal: options?.signal,
    }),

  heartbeatEditPresence: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<AgentEditPresence>(`/agents/${agentId}/edit-presence`, {
      method: 'POST',
      signal: options?.signal,
    }),

  releaseEditPresence: (agentId: string) =>
    request<void>(`/agents/${agentId}/edit-presence`, {
      method: 'DELETE',
    }),

  listByProject: (projectId: string) => request<Agent[]>(`/projects/${projectId}/agents`),

  create: async (projectId: string, data: AgentCreate) => {
    const newAgent = await request<Agent>(`/projects/${projectId}/agents`, {
      method: 'POST',
      body: JSON.stringify(data),
    })

    // Explicitly disable document chat by default for new agents (frontend override)
    documentsApi.updateDocumentConfig(newAgent.id, { document_chat_enabled: false }).catch((err: unknown) => {
      logger.error('Failed to disable document chat for new agent', err)
    })

    return newAgent
  },

  update: (agentId: string, data: AgentUpdate) =>
    request<Agent>(`/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: (
    agentId: string,
    data: {
      icon_file_id?: string | null
      catalog_metadata?: Record<string, unknown> | null
      tags?: string[] | null
    }
  ) =>
    request<Agent>(`/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Clone an existing agent into a new draft agent.
   * Preserves the complete graph definition including all node configurations,
   * tool references, knowledge sources, prompts, skills, and memory settings.
   */
  clone: (agentId: string, data?: AgentCloneRequest) =>
    request<AgentCloneResponse>(`/agents/${agentId}/clone`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  /** Assets referenced by the agent graph (for cross-project clone access selection). */
  getCloneDependencies: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<AgentCloneDependenciesResponse>(`/agents/${agentId}/clone-dependencies`, {
      signal: options?.signal,
    }),

  getDeletePrerequisites: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<AgentDeletePrerequisitesRead>(`/agents/${agentId}/delete-prerequisites`, {
      signal: options?.signal,
    }),

  /** Store + deployment blockers before unregistering from Agent Registry. */
  getUnregisterPrerequisites: (
    agentId: string,
    options?: { signal?: AbortSignal; tenantId?: string },
  ) =>
    request<AgentDeletePrerequisitesRead>(`/agents/${agentId}/unregister-prerequisites`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  simulate: (agentId: string, data: SimulationCreate) =>
    request<Simulation>(`/agents/${agentId}/simulate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (agentId: string) =>
    request<void>(`/agents/${agentId}`, {
      method: 'DELETE',
    }),

  unarchive: (agentId: string, data?: { name?: string }) =>
    request<Agent>(`/agents/${agentId}/unarchive`, {
      method: 'POST',
      body: JSON.stringify(data?.name ? { name: data.name } : {}),
    }),

  permanentDelete: (agentId: string) =>
    request<void>(`/agents/${agentId}/permanent-delete`, {
      method: 'POST',
    }),

  /** Submit a registration request. `catalog_metadata` seeds the registry catalog fields at registration time. */
  register: (agentId: string, data?: { catalog_metadata?: Record<string, unknown> | null }) =>
    request<Agent>(`/agents/${agentId}/register`, {
      method: 'POST',
      ...(data ? { body: JSON.stringify(data) } : {}),
    }),

  listRegistryRequests: (params?: {
    status?: 'pending' | 'approved' | 'rejected' | 'all'
    page?: number
    page_size?: number
    search?: string
  }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.page) q.set('page', String(params.page))
    if (params?.page_size) q.set('page_size', String(params.page_size))
    if (params?.search) q.set('search', params.search)
    const qs = q.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return request<{ items: AgentRegistryRequest[]; total: number }>(
      `/agents/registry-requests${querySuffix}`,
    )
  },

  approveRegistryRequest: (requestId: string) =>
    request<AgentRegistryRequest>(`/agents/registry-requests/${requestId}/approve`, {
      method: 'POST',
    }),

  rejectRegistryRequest: (requestId: string, reason: string) =>
    request<AgentRegistryRequest>(`/agents/registry-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  bulkDecideRegistryRequests: (params: {
    items: Array<{ request_id: string }>
    action: 'approve' | 'reject'
    reason?: string
  }) =>
    request<{ processed: number; skipped: number; errors: string[] }>(
      '/agents/registry-requests/bulk-decide',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    ),

  unregister: (agentId: string, options?: { tenantId?: string }) =>
    request<Agent>(`/agents/${agentId}/unregister`, {
      method: 'POST',
      tenantId: options?.tenantId,
    }),

  /** Bulk unregister (archive from registry). Agents remain published in Agent Studio. */
  bulkUnregister: (agentIds: string[], options?: { tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/agents/bulk-unregister', {
      method: 'POST',
      body: JSON.stringify({ agent_ids: agentIds }),
      tenantId: options?.tenantId,
    }),

  bulkDelete: (agentIds: string[]) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/agents/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ agent_ids: agentIds }),
    }),

  getAgentTemplates: (options?: { signal?: AbortSignal }) =>
    request<AgentTemplatesResponse>('/agents/templates', { signal: options?.signal }),

  listRegistrationTemplates: (options?: { signal?: AbortSignal }) =>
    request<RegistrationTemplateCatalogResponse>('/agents/registration-templates', {
      signal: options?.signal,
    }),

  previewRegistrationTemplate: (templateId: string, options?: { signal?: AbortSignal }) =>
    request<RegistrationTemplatePreviewResponse>(
      `/agents/registration-templates/${encodeURIComponent(templateId)}`,
      { signal: options?.signal },
    ),

  applyRegistrationTemplate: (
    templateId: string,
    data: RegistrationTemplateApplyRequest,
  ) =>
    request<RegistrationTemplateApplyResponse>(
      `/agents/registration-templates/${encodeURIComponent(templateId)}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  getGuardrailsSettings: (agentId: string) =>
    request<import('../../features/guardrails/types').GuardrailsSettings>(
      `/agents/${agentId}/guardrails-settings`,
    ),

  updateGuardrailsSettings: (
    agentId: string,
    settings: import('../../features/guardrails/types').GuardrailsSettings,
  ) =>
    request<import('../../features/guardrails/types').GuardrailsSettings>(
      `/agents/${agentId}/guardrails-settings`,
      { method: 'PUT', body: JSON.stringify(settings) },
    ),

  /** Suggest a structured SOTA agent description from the canvas graph. */
  generateDescription: (
    agentId: string,
    data?: { graph_definition?: Record<string, unknown> },
  ) =>
    request<{ description: string; source: 'ai' | 'heuristic' }>(
      `/agents/${agentId}/generate-description`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    ),

  /** Server CSV export with configure (catalog_metadata) columns. */
  exportRegistryCsv: async (params?: {
    project_id?: string
    search?: string
    registry_status?: 'registered' | 'archived'
    agent_ids?: string[]
  }): Promise<{ blob: Blob; filename: string }> => {
    const { agent_ids: agentIds, ...rest } = params ?? {}
    const query = buildQueryString({
      ...rest,
      ...(agentIds && agentIds.length > 0 ? { agent_ids: agentIds.join(',') } : {}),
    })
    const { blob, filename } = await requestBlob(`/agents/registry/export.csv${query}`)
    return { blob, filename: filename || 'Agent_Registry.csv' }
  },

  /** One agent: full catalog_metadata + resolved agentic flow (JSON file). */
  exportAgent: async (
    agentId: string,
    params?: { agent_type?: 'native' | 'external'; tenantId?: string },
  ): Promise<{ blob: Blob; filename: string }> => {
    const { tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    const path = `/agents/${encodeURIComponent(agentId)}/export${query}`
    const { blob, filename } = await requestBlob(path, { accept: 'application/json', tenantId })
    return { blob, filename: filename || 'agent_export.json' }
  },

  /** Every matching registry agent: same JSON shape as exportAgent (ignores page size). */
  exportRegistryAll: async (
    params?: RegistryJsonExportParams,
  ): Promise<{ blob: Blob; filename: string }> => {
    const query = buildQueryString(params || {})
    const { blob, filename } = await requestBlob(`/agents/registry/export-all${query}`, {
      accept: 'application/json',
    })
    return { blob, filename: filename || 'Agent_Registry_Export.json' }
  },

  downloadRegistryCsvTemplate: async (): Promise<{ blob: Blob; filename: string }> => {
    const { blob, filename } = await requestBlob('/agents/registry/csv-template')
    return { blob, filename: filename || 'Agent_Registry_Template.csv' }
  },

  importRegistryCsv: (file: File, params?: { project_id?: string; dry_run?: boolean }) => {
    const form = new FormData()
    form.append('file', file)
    const query = buildQueryString(params || {})
    return request<RegistryCsvImportResult>(`/agents/registry/import-csv${query}`, {
      method: 'POST',
      body: form,
    })
  },

  importRegistryJson: (file: File, params?: { project_id?: string; dry_run?: boolean }) => {
    const form = new FormData()
    form.append('file', file)
    const query = buildQueryString(params || {})
    return request<RegistryCsvImportResult>(`/agents/registry/import-json${query}`, {
      method: 'POST',
      body: form,
    })
  },
}

export interface RegistryCsvPreviewRow {
  row_number: number
  name?: string | null
  platform?: string | null
  project_id?: string | null
  allowlist_ok: boolean
  warning?: string | null
  error?: string | null
  duplicate?: boolean
  duplicate_reason?: string | null
}

export interface RegistryCsvImportResult {
  total_rows: number
  created: number
  updated?: number
  skipped: number
  duplicate_count?: number
  duplicates?: Array<{
    row_number: number
    name: string
    version: string
    project_id?: string | null
    existing_id?: string | null
    existing_type?: string | null
    existing_version?: string | null
    reason?: string
  }>
  errors: string[]
  preview_rows?: RegistryCsvPreviewRow[]
  allowlist_warning_count?: number
}

export type { Agent, AgentCreate, AgentUpdate } from './types'
